import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import {
  useMarkGapApplied,
  useResolveGap
} from '../../hooks/useScriptReview';
import type { Gap, GapStatus, GapTier, Script } from '../../types';
import { useUserStore } from '../../../../stores/userStore';

type GapsReportTabProps = {
  masterScript: Script;
};

const TIER_COLOR: Record<GapTier, 'error' | 'warning' | 'info'> = {
  BLOCKER: 'error',
  GAP: 'warning',
  GUESS: 'info'
};

const STATUS_COLOR: Record<
  GapStatus,
  'default' | 'success' | 'warning' | 'info'
> = {
  open: 'warning',
  answered: 'info',
  applied: 'success',
  rejected: 'default'
};

const STATUS_LABEL: Record<GapStatus, string> = {
  open: 'open',
  answered: 'answered (needs apply)',
  applied: 'applied',
  rejected: 'rejected'
};

const parseScriptData = (scriptData: string | null) => {
  if (!scriptData) return { gaps: [] as Gap[], gapsReport: '' };
  try {
    const parsed = JSON.parse(scriptData);
    const gaps = Array.isArray(parsed?.gaps) ? (parsed.gaps as Gap[]) : [];
    const gapsReport =
      typeof parsed?.gapsReport === 'string' ? parsed.gapsReport : '';
    return { gaps, gapsReport };
  } catch {
    return { gaps: [] as Gap[], gapsReport: '' };
  }
};

export const GapsReportTab = ({ masterScript }: GapsReportTabProps) => {
  const { t } = useTranslation();
  const { gaps, gapsReport } = useMemo(
    () => parseScriptData(masterScript.scriptData),
    [masterScript.scriptData]
  );

  const [showMarkdown, setShowMarkdown] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<Gap | null>(null);
  const [answer, setAnswer] = useState('');
  const [propagate, setPropagate] = useState(true);

  const resolveGap = useResolveGap(masterScript.id);
  const markApplied = useMarkGapApplied(masterScript.id);
  const userName = useUserStore(s => s.userName);

  const counts = useMemo(() => {
    const c = { BLOCKER: 0, GAP: 0, GUESS: 0, answered: 0, applied: 0 };
    for (const g of gaps) {
      if (g.status === 'answered') c.answered++;
      else if (g.status === 'applied') c.applied++;
      else if (g.status === 'open') c[g.tier]++;
    }
    return c;
  }, [gaps]);

  const openResolve = (gap: Gap) => {
    setResolveTarget(gap);
    setAnswer('');
    setPropagate(true);
  };

  const closeResolve = () => {
    setResolveTarget(null);
    setAnswer('');
  };

  const handleSubmitResolve = () => {
    if (!resolveTarget || !answer.trim()) return;
    resolveGap.mutate(
      {
        gapId: resolveTarget.id,
        body: { answer: answer.trim(), propagate }
      },
      { onSuccess: closeResolve }
    );
  };

  if (gaps.length === 0 && !gapsReport) {
    return (
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography color="text.secondary">
          {t(
            'scripts.gapsReportEmpty',
            'No gaps recorded. Run /procedure-to-script to generate one.'
          )}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap'
        }}
      >
        <ReportProblemOutlinedIcon color="warning" />
        <Typography variant="h6" sx={{ mr: 2 }}>
          {t('scripts.gapsReport', 'Gaps Report')}
        </Typography>
        {counts.BLOCKER > 0 && (
          <Chip
            size="small"
            label={`${counts.BLOCKER} blocker${counts.BLOCKER === 1 ? '' : 's'}`}
            color="error"
          />
        )}
        {counts.GAP > 0 && (
          <Chip
            size="small"
            label={`${counts.GAP} gap${counts.GAP === 1 ? '' : 's'}`}
            color="warning"
          />
        )}
        {counts.GUESS > 0 && (
          <Chip
            size="small"
            label={`${counts.GUESS} guess${counts.GUESS === 1 ? '' : 'es'}`}
            color="info"
          />
        )}
        {counts.answered > 0 && (
          <Chip
            size="small"
            label={`${counts.answered} answered`}
            color="info"
            variant="outlined"
          />
        )}
        {counts.applied > 0 && (
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon />}
            label={`${counts.applied} applied`}
            color="success"
            variant="outlined"
          />
        )}
      </Box>

      {gaps.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'scripts.gapsResolveNote',
            'Resolving a gap records your answer here and (optionally) writes it to the glossary so future ingestions learn it. It does NOT automatically patch STUB steps in the script. After resolving, ask Claude to apply the fix to the affected items.'
          )}
        </Alert>
      )}

      {gaps.length > 0 ? (
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 110 }}>Tier</TableCell>
                <TableCell sx={{ width: 160 }}>Kind</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell sx={{ width: 110 }}>Status</TableCell>
                <TableCell sx={{ width: 130 }} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {gaps.map(gap => (
                <TableRow key={gap.id} hover>
                  <TableCell>
                    <Chip
                      size="small"
                      label={gap.tier}
                      color={TIER_COLOR[gap.tier] ?? 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {gap.kind}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{gap.summary}</Typography>
                    {gap.resolution?.answer && (
                      <Typography
                        variant="caption"
                        color={
                          gap.status === 'applied' ? 'success.main' : 'info.main'
                        }
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        Answer: {gap.resolution.answer}
                        {gap.resolution.resolvedBy
                          ? ` — by ${gap.resolution.resolvedBy}`
                          : ''}
                        {gap.status === 'applied' && gap.resolution.appliedBy
                          ? ` · applied by ${gap.resolution.appliedBy}`
                          : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABEL[gap.status] ?? gap.status}
                      color={STATUS_COLOR[gap.status] ?? 'default'}
                      variant={gap.status === 'applied' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {gap.status === 'open' && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openResolve(gap)}
                      >
                        Resolve
                      </Button>
                    )}
                    {gap.status === 'answered' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() =>
                          markApplied.mutate({
                            gapId: gap.id,
                            appliedBy: userName ?? null
                          })
                        }
                        disabled={markApplied.isPending}
                      >
                        Mark applied
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        gapsReport && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t(
              'scripts.gapsStructuredMissing',
              'No structured gaps recorded — only the raw markdown report is available.'
            )}
          </Alert>
        )
      )}

      {gapsReport && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Toggle raw markdown report">
              <IconButton
                size="small"
                onClick={() => setShowMarkdown(v => !v)}
              >
                {showMarkdown ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              Raw markdown report
            </Typography>
          </Box>
          <Collapse in={showMarkdown}>
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: 1,
                borderColor: 'divider',
                '& h1': { fontSize: '1.5rem', mt: 2, mb: 1 },
                '& h2': { fontSize: '1.25rem', mt: 2, mb: 1 },
                '& h3': { fontSize: '1.1rem', mt: 1.5, mb: 0.75 },
                '& p': { mb: 1, lineHeight: 1.6 },
                '& ul, & ol': { pl: 3, mb: 1 },
                '& li': { mb: 0.5 },
                '& code': {
                  bgcolor: 'action.hover',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontSize: '0.875em'
                }
              }}
            >
              <ReactMarkdown>{gapsReport}</ReactMarkdown>
            </Box>
          </Collapse>
        </Box>
      )}

      <Dialog
        open={!!resolveTarget}
        onClose={closeResolve}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Resolve gap</DialogTitle>
        <DialogContent>
          {resolveTarget && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {resolveTarget.tier} · {resolveTarget.kind}
                </Typography>
                <Typography variant="body2">{resolveTarget.summary}</Typography>
              </Box>
              <TextField
                autoFocus
                fullWidth
                label={
                  resolveTarget.tier === 'GUESS'
                    ? 'Confirmed value (e.g., 2000 or B11_5)'
                    : 'Answer (e.g., B11_5 or CALIBRATOR_CMD:OUT ...)'
                }
                value={answer}
                onChange={e => setAnswer(e.target.value)}
                helperText={
                  resolveTarget.tier === 'GUESS'
                    ? 'The validated or corrected value for this guess. Recorded here; ask Claude to update the affected items if the value changes.'
                    : 'The action string that should replace STUB on the affected step. Recorded here; ask Claude to apply it to the script.'
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={propagate}
                    onChange={e => setPropagate(e.target.checked)}
                  />
                }
                label="Also save to glossary so future ingestions learn this"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResolve} disabled={resolveGap.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitResolve}
            disabled={!answer.trim() || resolveGap.isPending}
          >
            {resolveGap.isPending ? 'Resolving…' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
